/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  OnChanges,
  ChangeDetectionStrategy,
  SimpleChanges
} from '@angular/core';
import { ScrollTriggerService } from 'app/services/scroll-trigger.service';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { User } from '../../../../../models/User';
import { EventBusService } from '../../../../../services/EventBusService';
import { WatchlistService } from '../../../../../services/watchlist-service';
import { AppReviewBadgeComponent } from '../../../../shared/insight-badges/custom-badges/app-review-badge.component';
import { AppVpcBadgeComponent } from 'app/components/shared/insight-badges/custom-badges/app-vpc-badge.component';
import { LabelValue } from 'app/models/LabeLValue';

@Component({
  selector: 'app-ctv-information',
  templateUrl: './ctv-information.component.html',
  styleUrls: ['./ctv-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CtvInformationComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() appId: string;
  @Input() domain;
  @Input() region;
  @Input() data;
  @Input() month;
  @Input() device;
  @Input() numWatching;
  @Input() isFollowing: boolean;
  @Input() isFreemium: boolean;
  @Input() isAnonymous: boolean;
  @Input() processingFollow: boolean;
  @Input() selectedCountry: LabelValue;
  @Output() goToDiscovery: EventEmitter<any> = new EventEmitter<any>();
  @Output() anchorClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() followButtonClick: EventEmitter<Event> = new EventEmitter<Event>();

  @Output() followButtonBreakpointReached: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  @ViewChild('followButton') followButton: ElementRef<HTMLElement>;

  hideReadMoreLink: boolean;
  currentScreenshotIndex = 0;
  viewCount = -1;
  badges = [];
  loading: boolean;
  hasDangerousPermissions: boolean;

  destroyToken: Subject<void>;

  constructor(
    public _userService: User,
    private _eventBus: EventBusService,
    private _scrollTrigger: ScrollTriggerService,
    private _watchlistService: WatchlistService
  ) {
    this._eventBus.watchlistUpdate.subscribe(
      this.updateFollowingState.bind(this)
    );
  }

  updateFollowingState() {
    this.isFollowing = this._watchlistService.isFollowing(this.appId, 'ctv');
  }

  ngOnInit() {
    this.destroyToken = new Subject();

    this.updateFollowingState();
    this.selectedCountry = {
      label: 'All Countries',
      value: 'GLOBAL'
    };
  }

  ngAfterViewInit() {
    this._scrollTrigger.callback$
      .pipe(
        takeUntil(this.destroyToken),
        map(() => {
          console.assert(
            this.followButton,
            'no follow button reference defined on the page'
          );
          const elBottom =
            this.followButton.nativeElement.getBoundingClientRect().bottom;

          return elBottom < 0;
        })
      )
      .subscribe(value => {
        this.followButtonBreakpointReached.emit(value);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      let coppaBadge;

      const { coppaTargetChildrenRisk } = this.data.overview;
      const { isReviewedManually, vpcDetected, passesLocation, ipTraffic } =
        this.data.coppaPermissions;
      const { hasPrivacyLink, isExistInAppStore, hasAdsTxt } =
        this.data.riskOverview;

      if (!coppaTargetChildrenRisk || coppaTargetChildrenRisk == 'N/A') {
        coppaBadge = {
          detectedText: 'N/A',
          icon: '/assets/images/unknown.svg',
          hidden: false
        };
      } else {
        const isCoppaTargetChildrenRiskLow = coppaTargetChildrenRisk == 'low';
        coppaBadge = {
          detectedText: isCoppaTargetChildrenRiskLow
            ? 'General Audience'
            : 'Child Directed',
          icon:
            coppaTargetChildrenRisk === 'high'
              ? '/assets/images/icon-child-audience.svg'
              : '/assets/images/icon-general-audience.svg',
          reverse: !isCoppaTargetChildrenRiskLow,
          hidden: !coppaTargetChildrenRisk
        };
      }

      this.badges = [
        {
          text: 'COPPA Audience',
          locked: this.isFreemium,
          detected: true,
          badgeColor: 'blue',
          anchorLink: '#app-coppa-permission-risk',
          ...coppaBadge
        },
        {
          component: AppReviewBadgeComponent,
          locked: this.isFreemium,
          hidden: !isReviewedManually
        },
        {
          component: AppVpcBadgeComponent,
          locked: this.isFreemium,
          hidden:
            coppaTargetChildrenRisk !== 'high' ||
            vpcDetected == undefined ||
            vpcDetected === 'N/A'
        },
        {
          text: 'Privacy Policy',
          locked: this.isFreemium,
          detected: hasPrivacyLink
        },
        {
          text:
            this.device === 'roku'
              ? 'In Roku Store'
              : this.device == 'firetv'
              ? 'In FireTV Store'
              : this.device == 'samsung'
              ? 'In Samsung Store'
              : 'In tvOS Store',
          locked: this.isFreemium,
          detected: isExistInAppStore
        },
        {
          text: 'App-ads.txt',
          locked: this.isFreemium,
          detected: hasAdsTxt
        },
        {
          text: 'Transmits Location',
          locked: this.isFreemium,
          detected: passesLocation,
          reverse: true,
          anchorLink: '#app-coppa-permission-risk'
        },
        {
          text: 'Transmits Residential IP',
          locked: this.isFreemium,
          detected: ipTraffic,
          reverse: true,
          anchorLink: '#app-coppa-permission-risk'
        }
      ];
    }

    this.updateFollowingState();
  }

  ngOnDestroy(): void {
    this.destroyToken.next();
    this.destroyToken.complete();
  }

  goToDiscoveryClick(data: any) {
    this.goToDiscovery.emit(data);
  }

  goToSection(section: string) {
    this.anchorClick.emit(section);
  }

  onAnchorClick(anchor: string) {
    this.anchorClick.emit(anchor);
  }
}
